import React, { useCallback, useEffect, useRef, useState } from 'react'
import { styled } from '@mui/material/styles'
import {
  Grid,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography,
  Button as MuiButton,
  Alert,
  Box,
  CardContent
} from '@mui/material'
import { spacing } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { RootState } from '../../redux'
import { QrReader } from 'react-qr-reader'
import { getProject, setProject } from '../../redux/slices/projects'
import { setDevice } from '../../redux/slices/devices'
import PowerSupplyForm from './PowerSupplyForm'
import LevelForm from './LevelForm'
import LiftForm from './LiftForm'
import { resetSteps, setData, setStep } from '../../redux/slices/steps'
import { QRData } from '../../enums/QRData'
import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  extractCompleteShortCode,
  sanitizeShortCodeInput,
  SHORT_CODE_LENGTH
} from '../../utils/deviceCode'
import DeviceCodeInput from '../DeviceCodeInput'
import deviceService from '../../services/deviceService'

/**
 * The styled Card
 */
const Card = styled(MuiCard)(spacing)

/**
 * The styled Divider
 */
const Divider = styled(MuiDivider)(spacing)

/**
 * The styled Button
 */
const Button = styled(MuiButton)(spacing)

/**
 * The properties
 */
interface InstallDeviceWithProjectInstructionsProps {
  projectId: number
  handleBackStep(): void
  initialLevel?: number | null
  initialLift?: number | null
}

const INSTALL_DEVICE_HISTORY_KEY = 'install-device-history-step' as const
const MIN_INSTALL_DEVICE_STEP = 0
const MAX_INSTALL_DEVICE_STEP = 4

type InstallDeviceHistoryState = {
  key: typeof INSTALL_DEVICE_HISTORY_KEY
  step: number
}

function clampInstallDeviceStep(value: number): number {
  if (value <= MIN_INSTALL_DEVICE_STEP) {
    return MIN_INSTALL_DEVICE_STEP
  }

  if (value >= MAX_INSTALL_DEVICE_STEP) {
    return MAX_INSTALL_DEVICE_STEP
  }

  return value
}

function createInstallDeviceHistoryState(step: number): InstallDeviceHistoryState {
  return {
    key: INSTALL_DEVICE_HISTORY_KEY,
    step
  }
}

export default function InstallDeviceToProjectInstructions({
  projectId,
  handleBackStep,
  initialLevel,
  initialLift
}: InstallDeviceWithProjectInstructionsProps) {
  /**
   * The tanslate function
   */
  const [t] = useTranslation('common')

  /**
   * The navigate function
   */
  const navigate = useNavigate()

  /**
   * The dispatch function
   */
  const dispatch = useDispatch()

  /**
   * The step number
   * The QR data (not scanned or device name)
   */
  const { step, data } = useSelector((state: RootState) => state.steps)

  /**
   * The device
   */
  const { device } = useSelector((state: RootState) => state.devices)

  /**
   * The project
   */
  const { project } = useSelector((state: RootState) => state.projects)

  /**
   * The shortUUID of the device
   */
  const [shortUUID, setShortUUID] = useState('')

  /**
   * The Show alert state
   */
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [lookupError, setLookupError] = useState<boolean>(false)

  const lastRequestedShortUUIDRef = useRef<string>('')

  const historyInitializedRef = useRef(false)
  const isHandlingPopRef = useRef(false)
  const previousStepRef = useRef(step)

  const pushHistoryForStep = useCallback((nextStep: number, options?: { replace?: boolean }) => {
    if (!historyInitializedRef.current || typeof window === 'undefined') {
      return
    }

    const state = createInstallDeviceHistoryState(clampInstallDeviceStep(nextStep))

    if (options?.replace) {
      window.history.replaceState(state, '')
    } else {
      window.history.pushState(state, '')
    }
  }, [])

  useEffect(() => {
    if (projectId) {
      dispatch(getProject(projectId))
    }
  }, [])

  useEffect(() => {
    if (data === QRData.NOT_SCANNED) {
      return
    }

    const completeCode = extractCompleteShortCode(data)

    if (completeCode) {
      setShortUUID(completeCode)
      handleShortUUID(completeCode)
      return
    }

    const sanitized = sanitizeShortCodeInput(data)
    setShortUUID(sanitized)
    lastRequestedShortUUIDRef.current = ''
    setLookupError(false)
  }, [data])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as InstallDeviceHistoryState | null

      if (!state || state.key !== INSTALL_DEVICE_HISTORY_KEY) {
        return
      }

      const targetStep = clampInstallDeviceStep(state.step)

      if (targetStep === step) {
        return
      }

      isHandlingPopRef.current = true

      if (step > targetStep && step === 1) {
        dispatch(setData(QRData.NOT_SCANNED))
        dispatch(setDevice(undefined))
      }

      dispatch(setStep(targetStep))
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [dispatch, step])

  useEffect(() => {
    if (historyInitializedRef.current || typeof window === 'undefined') {
      previousStepRef.current = step
      return
    }

    const initialStep = clampInstallDeviceStep(step)

    window.history.replaceState(createInstallDeviceHistoryState(MIN_INSTALL_DEVICE_STEP), '')

    for (let current = MIN_INSTALL_DEVICE_STEP + 1; current <= initialStep; current++) {
      window.history.pushState(createInstallDeviceHistoryState(current), '')
    }

    if (initialStep !== MIN_INSTALL_DEVICE_STEP) {
      window.history.replaceState(createInstallDeviceHistoryState(initialStep), '')
    }

    historyInitializedRef.current = true
    previousStepRef.current = initialStep
  }, [step])

  useEffect(() => {
    if (!historyInitializedRef.current || typeof window === 'undefined') {
      previousStepRef.current = step
      return
    }

    if (isHandlingPopRef.current) {
      isHandlingPopRef.current = false
      previousStepRef.current = step
      return
    }

    const previousStep = previousStepRef.current
    const clampedStep = clampInstallDeviceStep(step)

    if (clampedStep === previousStep) {
      return
    }

    pushHistoryForStep(clampedStep)
    previousStepRef.current = clampedStep
  }, [pushHistoryForStep, step])

  const handleNavigation = (step: number) => {
    if (step === 0) {
      //Navigate to installation main page
      dispatch(resetSteps())
      dispatch(setProject(undefined))
      handleBackStep()
    } else if (step === 1) {
      dispatch(setData(QRData.NOT_SCANNED))
      dispatch(setDevice(undefined))
      if (historyInitializedRef.current && typeof window !== 'undefined') {
        isHandlingPopRef.current = true
        window.history.back()
      } else {
        dispatch(setStep(step - 1))
      }
    } else if (step === 4) {
      //if we are at lift form we need to go 2 steps back so we land to powerSupply form
      if (historyInitializedRef.current && typeof window !== 'undefined') {
        isHandlingPopRef.current = true
        window.history.go(-2)
      } else {
        dispatch(setStep(step - 2))
      }
    } else {
      if (historyInitializedRef.current && typeof window !== 'undefined') {
        isHandlingPopRef.current = true
        window.history.back()
      } else {
        dispatch(setStep(step - 1))
      }
    }
  }

  function handleShortUUID(input: string) {
    const completeCode = extractCompleteShortCode(input)

    if (!completeCode) {
      return
    }

    if (lastRequestedShortUUIDRef.current === completeCode) {
      return
    }

    lastRequestedShortUUIDRef.current = completeCode
    setLookupError(false)

    deviceService
      .getDevice(completeCode)
      .then((device) => {
        dispatch(setDevice(device))
      })
      .catch(() => {
        lastRequestedShortUUIDRef.current = ''
        setLookupError(true)
      })
  }

  useEffect(() => {
    if (!device) {
      return
    }

    if (device.asset?.project) {
      if (step === 0) {
        dispatch(setDevice(undefined))
        setShowAlert(true)
        setLookupError(false)
      }

      return
    }

    if (step === 0) {
      dispatch(setStep(step + 1))
      setLookupError(false)
    }
  }, [device, dispatch, step])

  return (
    <React.Fragment>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {step < 4 && (
                <React.Fragment>
                  <Button size="small" variant="text" onClick={() => handleNavigation(step)}>
                    <ArrowBackIcon />
                    {t('Manufacturing.back')}
                  </Button>
                  <Divider sx={{ my: 6 }} />
                </React.Fragment>
              )}

              <Box justifyContent={'center'} alignItems="center">
                {step === 0 && (
                  <>
                    <Typography align="center" variant="h6">
                      {t('Manufacturing.scanQR')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Box sx={{ width: '100%', maxWidth: 320 }}>
                        <QrReader
                          scanDelay={500}
                          onResult={(result) => {
                            if (result) {
                              dispatch(setData(result.toString()))
                            }
                          }}
                          constraints={{ facingMode: 'environment', aspectRatio: 9 / 16 }}
                          containerStyle={{ width: '100%' }}
                          videoContainerStyle={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '177.78%',
                            backgroundColor: 'black',
                            borderRadius: 8,
                            overflow: 'hidden'
                          }}
                          videoStyle={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    </Box>
                    <Divider sx={{ my: 6 }} />
                    <Typography align="center" variant="h6">
                      {t('Manufacturing.enterShortCode')}
                    </Typography>
                    <Divider sx={{ my: 6 }} />
                    <Grid container spacing={6} justifyContent="center">
                      {showAlert ? (
                        <Grid item xs={12}>
                          <Alert severity="warning" onClose={() => setShowAlert(false)}>
                            {t('deviceAlreadyInstalled')}
                          </Alert>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={8} md={6} lg={4}>
                          <DeviceCodeInput
                            sx={{ mt: 4, mb: 4, mx: 'auto' }}
                            value={shortUUID}
                            onChange={(value) => {
                              const sanitized = sanitizeShortCodeInput(value)
                              setShortUUID(sanitized)
                              setLookupError(false)

                              if (sanitized.length === SHORT_CODE_LENGTH) {
                                handleShortUUID(sanitized)
                              } else {
                                lastRequestedShortUUIDRef.current = ''
                              }
                            }}
                          />
                          {lookupError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                              {t('deviceWasNotFound')}
                            </Alert>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}

                {step === 1 && device && project && (
                  <PowerSupplyForm
                    device={device}
                    project={project}
                    initialLevel={initialLevel}
                    initialLift={initialLift}
                    onCancel={() => handleNavigation(step)}
                  />
                )}

                {step === 2 && project && <LevelForm project={project} />}
                {step === 3 && project && <LiftForm project={project} />}
                {step === 4 && project && (
                  <Grid container spacing={6}>
                    <Grid item xs={6} sm={6} md={6} lg={6}>
                      <Typography align="left">
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => {
                            dispatch(resetSteps())
                            if (historyInitializedRef.current && typeof window !== 'undefined') {
                              historyInitializedRef.current = false
                              window.history.replaceState(
                                createInstallDeviceHistoryState(MIN_INSTALL_DEVICE_STEP),
                                ''
                              )
                              historyInitializedRef.current = true
                              previousStepRef.current = MIN_INSTALL_DEVICE_STEP
                            }
                          }}
                        >
                          {t('Manufacturing.addNextDevice')}
                          <AddIcon />
                        </Button>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6}>
                      <Typography align="right">
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          {t('Manufacturing.goToProject')}
                          <ArrowForwardIcon />
                        </Button>
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  )
}
